import { useState } from "react";
import { toast } from "sonner";
import { saveWalkinOffice, saveWalkinMeasurements } from "@/lib/api";

// One pre-sale questionnaire at a glance: quick facts first, then the member's own words, then the
// office use: water given (yes / no) and notes.

const List = ({ label, items }) => (items && items.length ? (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
    <ul className="mt-1 space-y-0.5 text-sm text-zinc-100">{items.map((i) => <li key={i}>“{i}”</li>)}</ul>
  </div>
) : null);

const Line = ({ label, value }) => (value ? (
  <div className="text-sm"><span className="text-zinc-500">{label}: </span><span className="text-zinc-100">{value}</span></div>
) : null);

// What the member said on the questionnaire about recording the consult.
export function RecordingConsent({ form }) {
  const when = form.recording_consent_at
    ? new Date(form.recording_consent_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  const [tone, text] = form.recording_consent === true ? ["border-emerald-500/40 bg-emerald-500/10 text-emerald-200", `Recording: member agreed on the questionnaire${when ? ` (${when})` : ""}`]
    : form.recording_consent === false ? ["border-[#333] bg-[#0a0a0a] text-zinc-300", "Recording: member said no. Don't record."]
    : ["border-amber-500/40 bg-amber-500/10 text-amber-200", "Recording: not asked on the questionnaire. Ask on the recording."];
  return <div className={`rounded-lg border px-3 py-2 text-sm font-medium ${tone}`} data-testid="walkin-recording-consent-line">{text}</div>;
}

// Weight and waist from the front desk (CR-0022). Missing (the phone step was skipped)? Staff add
// them here; they carry to the consult and the Accountability starting numbers.
function Measurements({ form, editable }) {
  const [m, setM] = useState(form.measurements || null);
  const [w, setW] = useState("");
  const [c, setC] = useState("");
  const [busy, setBusy] = useState(false);
  if (m) {
    return <div className="text-sm" data-testid="walkin-measurements"><span className="text-zinc-500">Weight: </span>
      <span className="text-zinc-100 font-semibold">{m.weight_kg} kg</span><span className="text-zinc-500"> · Waist: </span>
      <span className="text-zinc-100 font-semibold">{m.waist_cm} cm</span></div>;
  }
  if (!editable) return <div className="text-sm text-amber-300">Weight and waist: not taken yet</div>;
  const save = async () => {
    setBusy(true);
    try { setM((await saveWalkinMeasurements(form.id, { weight_kg: parseFloat(w), waist_cm: parseFloat(c) })).measurements); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the measurements"); }
    finally { setBusy(false); }
  };
  const box = "w-24 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 py-1.5 text-sm text-zinc-100";
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="walkin-measurements-missing">
      <div className="text-sm text-amber-200">Weight and waist not taken yet. Measure at the desk and add them:</div>
      <div className="flex flex-wrap items-center gap-2">
        <input type="number" inputMode="decimal" step="0.1" placeholder="Weight kg" value={w} onChange={(e) => setW(e.target.value)} className={box} />
        <input type="number" inputMode="decimal" step="0.5" placeholder="Waist cm" value={c} onChange={(e) => setC(e.target.value)} className={box} />
        <button type="button" disabled={busy || !w || !c} onClick={save}
          className="rounded-full bg-rose-600 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50">Save</button>
      </div>
    </div>
  );
}

// compact: the parts a coach uses mid-conversation (Sales mode), without contact details,
// GoHighLevel status or the office-use section.
export default function WalkinSummary({ form, editable = true, compact = false }) {
  const [office, setOffice] = useState({ hydrated: null, notes: "", ...(form.office || {}) });
  const [saving, setSaving] = useState(false);
  const goals = [...(form.goals || []), ...(form.goal_other ? [form.goal_other] : [])];
  const save = async (next) => {
    setOffice(next);
    setSaving(true);
    try { await saveWalkinOffice(form.id, { hydrated: next.hydrated, notes: next.notes || "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the checklist"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="walkin-summary">
      {!compact && <RecordingConsent form={form} />}
      <Measurements form={form} editable={editable && !compact} />
      <div className="flex flex-wrap items-center gap-2">
        {form.commitment != null && (
          <span className={`rounded-full px-3 py-1 text-sm font-semibold ${form.commitment >= 8 ? "bg-emerald-500/15 text-emerald-300" : form.commitment >= 5 ? "bg-amber-500/15 text-amber-300" : "bg-rose-500/15 text-rose-300"}`}>
            Commitment {form.commitment}/10
          </span>
        )}
        {goals.map((g) => <span key={g} className="rounded-full border border-[#2a2a2a] px-2.5 py-0.5 text-xs text-zinc-300">{g}</span>)}
      </div>
      {form.why_top && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-rose-300">Hits hardest</div>
          <p className="mt-1 text-lg font-semibold text-white">“{form.why_top}”</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        <Line label="Last felt at their best" value={form.since_best} />
        <Line label="Started and stopped" value={form.tried_before ? form.restarts : form.tried_before === false ? "Never seriously tried" : ""} />
        <Line label="Tried" value={[...(form.tried || []), form.tried_text].filter(Boolean).join(", ")} />
        <Line label="Events in next 6 weeks" value={form.events_soon ? `Yes${form.events_when ? `, ${form.events_when}` : ""}` : form.events_soon === false ? "No" : ""} />
        <Line label="If nothing changes in a year, worried about" value={form.worry} />
        <Line label="Feels" value={(form.feelings || []).join(", ")} />
      </div>
      <div className="space-y-3 rounded-xl border border-[#262626] bg-[#0a0a0a] p-4">
        <List label="Why now (true for them)" items={(form.why || []).filter((w) => w !== form.why_top)} />
        <List label="What got in the way" items={[...(form.stopped || []), ...(form.stopped_other ? [form.stopped_other] : [])]} />
        <List label="Would make 6 weeks worth it" items={form.worth_it || []} />
        <Line label="Long term" value={form.long_term} />
        {form.note && <Line label="Note to the coach" value={form.note} />}
      </div>
      {compact ? null : <>
      <div className="text-xs text-zinc-500">{form.phone} · {form.email}{form.dob ? ` · born ${form.dob}` : ""}</div>
      {form.ghl_showed && form.ghl_showed.state !== "off" && (
        <div className={`text-xs ${form.ghl_showed.state === "error" ? "text-rose-300" : "text-emerald-300"}`}>
          GoHighLevel: {form.ghl_showed.message}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Office use {saving && <span className="normal-case tracking-normal">· saving…</span>}</div>
        {office.by === "member" ? (
          <div className={`text-sm ${office.hydrated ? "text-emerald-300" : "text-amber-300"}`} data-testid="water-member">
            Water: member says {office.hydrated ? "yes, they got one" : "no, they didn't get one"}
          </div>
        ) : (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Water given">
          <span className="text-sm text-zinc-300 mr-1">Water given</span>
          {[[true, "Yes"], [false, "No"]].map(([v, label]) => (
            <button key={label} type="button" disabled={!editable} onClick={() => save({ ...office, hydrated: office.hydrated === v ? null : v })}
              aria-pressed={office.hydrated === v} data-testid={`water-${label.toLowerCase()}`}
              className={`rounded-full border px-3 py-1 text-xs ${office.hydrated === v
                ? (v ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-amber-500/50 bg-amber-500/10 text-amber-300")
                : "border-[#2a2a2a] text-zinc-400"}`}>
              {label}
            </button>
          ))}
        </div>
        )}
        {editable && (
          <input defaultValue={office.notes || ""} onBlur={(e) => e.target.value !== (office.notes || "") && save({ ...office, notes: e.target.value })}
            placeholder="Notes" className="mt-2 w-full rounded-md bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-sm text-zinc-100" />
        )}
      </div>
      </>}
    </div>
  );
}
